import { Box, Card, Icon, IconType, Paper } from 'lucid-components-react';
import React, {
  FC,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useState,
  ReactText
} from 'react';
import { Link } from '@lucid/gatsby-theme-lucid';

import { mqMax } from 'theme/base';
import { LucidTheme } from 'types/types';

interface Tab {
  key: ReactText;
  header: string;
  link?: string;
  linkText?: string;
  children: ReactNode;
}

interface Props {
  tabs: Tab[];
  gutterRems?: number;
}

const CardTabs: FC<Props & React.HTMLAttributes<HTMLDivElement>> = ({
  tabs,
  gutterRems = 1.25,
  ...rest
}) => {
  const [activeTabKey, setActiveTabKey] = useState('');

  useEffect(() => {
    setActiveTabKey('');
  }, [tabs]);

  return (
    <Box
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap'
      }}
      {...rest}
    >
      {tabs.map(({ key, header, link, linkText, children }, i) => {
        const currentKey = typeof key !== 'string' ? key.toString() : key;
        const isActive = currentKey === activeTabKey;
        const isEven = i % 2 === 0;
        const [side, otherSide] = isEven
          ? ['Right', 'Left']
          : ['Left', 'Right'];

        const cardClickHandler = () => {
          if (!isActive) {
            setActiveTabKey(currentKey);
          }
        };

        const cardKeyHandler = (event: KeyboardEvent) => {
          if (
            !isActive &&
            (event.key === ' ' ||
              event.key === 'Enter' ||
              event.key === 'Spacebar')
          ) {
            event.preventDefault();
            setActiveTabKey(currentKey);
          }
        };

        return (
          <Box
            key={currentKey}
            css={{
              marginBottom: `${gutterRems}rem`,
              flex: `0 1 calc(50% - ${gutterRems / 2}rem)`,
              flexBasis: `calc(50% - ${gutterRems / 2}rem)`,
              [mqMax.tabletLandscape]: {
                flex: '0 1 100%',
                flexBasis: '100%'
              }
            }}
          >
            <Card
              key={`card-${currentKey}`}
              css={(theme: LucidTheme) => ({
                height: '7rem',
                [mqMax.mobile]: {
                  height: '10.5rem'
                },
                ...(isActive && {
                  boxShadow: theme.shadows[10],
                  position: 'relative',
                  height: '8.125rem',
                  paddingBottom: '18px',
                  borderBottom: '1px solid #FFF',
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  [mqMax.tabletLandscape]: {
                    height: '7rem',
                    paddingBottom: '0'
                  },
                  [mqMax.mobile]: {
                    height: '10.5rem'
                  }
                })
              })}
              onClick={cardClickHandler}
              onKeyDown={cardKeyHandler}
              role={isActive ? 'article' : 'button'}
              tabIndex={isActive ? undefined : 0}
              variant={!isActive ? 'interactive' : 'default'}
            >
              <Card.Content
                css={(theme: LucidTheme) => [
                  {
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: `0 ${theme.space[5]}px`
                  },
                  isActive && {
                    [mqMax.tabletLandscape]: {
                      paddingTop: 0,
                      paddingBottom: 0
                    }
                  }
                ]}
              >
                <button
                  css={(theme: LucidTheme) => ({
                    fontSize: theme.fontSizes[4],
                    border: 0,
                    background: 'none',
                    outline: 'none',
                    '&:hover': {
                      color: theme.colors.primary,
                      cursor: 'pointer'
                    },
                    '&:focus': {
                      color: theme.colors.primary
                    }
                  })}
                  onClick={evt => {
                    if (isActive) {
                      setActiveTabKey('');
                      evt.stopPropagation();
                    }
                  }}
                >
                  <Icon
                    css={{
                      flex: '0 1 5%',
                      transition: 'transform 0.5s',
                      transform: isActive ? 'rotate(90deg)' : 'rotate(0deg)'
                    }}
                    type={IconType.CarotRight}
                  />
                </button>
                <Box
                  css={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    paddingLeft: '2rem'
                  }}
                >
                  <span
                    css={(theme: LucidTheme) => ({
                      display: 'inline',
                      fontSize: theme.fontSizes[4],
                      fontWeight: theme.fontWeights.bold,
                      flex: '0 1 37.5%',
                      [mqMax.mobile]: {
                        flex: '0 1 100%',
                        minHeight: '3.5rem'
                      }
                    })}
                  >
                    {header}
                  </span>
                  {link && linkText && (
                    <Link
                      primary
                      css={(theme: LucidTheme) => ({
                        display: 'inline-flex',
                        alignItems: 'center',
                        flex: '0 1 57.5%',
                        whiteSpace: 'pre-wrap',
                        justifyContent: 'flex-end',
                        textAlign: 'right',
                        fontSize: theme.fontSizes[4],
                        [mqMax.mobile]: {
                          margin: '0.5rem 0 0'
                        }
                      })}
                      to={link}
                    >
                      {linkText}
                    </Link>
                  )}
                </Box>
              </Card.Content>
            </Card>
            <Paper
              key={`paper-${currentKey}`}
              css={(theme: LucidTheme) => ({
                display: isActive ? 'flex' : 'none',
                boxShadow: theme.shadows[10],
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  background: theme.colors.white,
                  top: '-1px',
                  height: '1px',
                  [`${otherSide.toLowerCase()}`]: 0,
                  [`${side.toLowerCase()}`]: `calc(50% + ${gutterRems / 2}rem)`,
                  [mqMax.tabletLandscape]: {
                    [`${side.toLowerCase()}`]: 0
                  }
                },
                justifyContent: 'space-between',
                [`borderTop${otherSide}Radius`]: 0,
                [`margin${side}`]: `calc(-100% - ${gutterRems}rem)`,
                [mqMax.tabletLandscape]: {
                  marginRight: 0,
                  marginLeft: 0,
                  borderTopRightRadius: 0,
                  borderTopLeftRadius: 0
                },
                [mqMax.mobile]: {
                  flexWrap: 'wrap'
                }
              })}
            >
              {children}
            </Paper>
          </Box>
        );
      })}
    </Box>
  );
};

export default CardTabs;
